<template>
  <!-- badge: resumen para tablas (sin versiones: estado y antigüedad) -->
  <span v-if="mode == 'badge'">
    <span v-if="!status" class="text-muted">—</span>
    <span v-else class="monitor-status-cell"
          v-b-tooltip.hover.html="{ title: tooltip, placement: 'left', boundary: 'window', customClass: 'monitor-status-tip' }">
      <span v-for="app in apps" :key="app.key" class="monitor-status-cell__app">
        <span class="monitor-status-cell__dot" :class="'monitor-status-cell__dot--' + app.tone"></span>
        <span class="monitor-status-cell__name">{{app.label}}</span>
        <span v-if="app.age" class="monitor-status-cell__age" :class="'monitor-status-cell__age--' + app.age.tone">
          <AppIcon name="update" /> {{app.age.label}}
        </span>
        <span v-else class="monitor-status-cell__age monitor-status-cell__age--none">sin fecha</span>
      </span>
    </span>
  </span>

  <!-- detail: estado completo -->
  <div v-else>
    <div v-if="!status" class="text-muted">Sin datos reportados</div>
    <div v-else class="monitor-detail">
      <div class="monitor-detail__row">
        <span class="monitor-detail__label"><AppIcon name="deployed_code" /> {{appLabel}}</span>
        <span class="monitor-detail__value">
          <span v-if="status.sistema.estado" class="monitor-pill" :class="'monitor-pill--' + stateVariant(status.sistema.estado)">
            <AppIcon :name="status.sistema.estado == 'activo' ? 'check_circle' : 'cancel'" /> {{stateLabel(status.sistema.estado)}}
          </span>
          <span class="monitor-detail__version" v-b-tooltip.hover :title="status.sistema.fecha_version ? 'Actualizada el ' + formatDate(status.sistema.fecha_version) : ''">v{{status.sistema.version}}</span>
          <span v-if="status.sistema.fecha_version" class="text-muted monitor-status-detail__age">actualizada {{timeAgo(status.sistema.fecha_version)}}</span>
        </span>
      </div>

      <div v-if="'intranet' in status" class="monitor-detail__row">
        <span class="monitor-detail__label"><AppIcon name="lan" /> Sitio web</span>
        <span class="monitor-detail__value">
          <span v-if="!status.intranet" class="text-muted">No tiene</span>
          <template v-else>
            <span class="monitor-pill" :class="'monitor-pill--' + stateVariant(status.intranet.estado)">
              <AppIcon :name="status.intranet.estado == 'activo' ? 'check_circle' : 'cancel'" /> {{stateLabel(status.intranet.estado)}}
            </span>
            <span class="monitor-detail__version" v-b-tooltip.hover :title="status.intranet.fecha_version ? 'Actualizada el ' + formatDate(status.intranet.fecha_version) : ''">v{{status.intranet.version}}</span>
          <span v-if="status.intranet.fecha_version" class="text-muted monitor-status-detail__age">actualizada {{timeAgo(status.intranet.fecha_version)}}</span>
          </template>
        </span>
      </div>

      <div class="monitor-detail__row monitor-detail__row--top">
        <span class="monitor-detail__label"><AppIcon name="language" /> Dominios</span>
        <span class="monitor-detail__value">
          <span v-if="!status.dominios.length" class="text-muted">Sin dominios</span>
          <span v-else :class="domainsDown.length ? 'text-danger' : 'text-success'">
            {{status.dominios.length - domainsDown.length}} de {{status.dominios.length}} apuntan al sistema
          </span>
        </span>
      </div>
      <div v-if="status.dominios.length" class="monitor-detail__list">
        <div v-for="domain in status.dominios" :key="domain.dominio" class="monitor-detail__item">
          <span class="text-truncate">
            <AppIcon :name="domain.apunta ? 'link' : 'link_off'" :class="domain.apunta ? 'text-success' : 'text-danger'" class="mr-1" />{{domain.dominio}}
          </span>
          <span class="monitor-pill" :class="domain.apunta ? 'monitor-pill--success' : 'monitor-pill--danger'">{{domain.apunta ? 'Apunta' : 'No apunta'}}</span>
        </div>
      </div>

      <div v-if="status.fecha_actualizacion" class="monitor-detail__footer">
        <AppIcon name="update" /> Relevado {{timeAgo(status.fecha_actualizacion)}} · {{formatDate(status.fecha_actualizacion)}}
      </div>
    </div>
  </div>
</template>

<script>
  // formato de monitor_status (definido con Monitor):
  //  zyncro:         {sistema: {estado, version}, intranet: {estado, version}|null, dominios, fecha_actualizacion}
  //  laravel/docker: {sistema: {version}, dominios, fecha_actualizacion}
  //  dominios: [{dominio, apunta}]
  import { timeAgo, formatDate, versionAge, versionDate, VERSION_FRESH_DAYS, VERSION_OLD_DAYS } from './monitorConfig'
  import './monitorDetail.css'

  const STATES = {
    activo: { variant: 'success', label: 'Activo' },
    inactivo: { variant: 'danger', label: 'Inactivo' },
  }

  export default {
    props: {
      value: { default: null },
      mode: { type: String, default: 'badge' },
      // tipo de instancia: zyncro distingue sistema e intranet, laravel/docker es una aplicación
      type: { type: String, default: '' },
    },
    computed: {
      appLabel() {
        return this.type && this.type != 'zyncro' ? 'Aplicación' : 'Sistema'
      },
      status() {
        var data = this.value
        if(data === null || data === undefined || data === '') return null
        if(typeof data == 'string') {
          try {
            data = JSON.parse(data)
          } catch (error) {
            return null
          }
        }
        if(!data || typeof data != 'object' || !data.sistema) return null
        // copia (no se toca el prop) con la fecha de versión resuelta
        data = Object.assign({}, data, { dominios: Array.isArray(data.dominios) ? data.dominios : [] })
        var appKeys = ['sistema', 'intranet']
        appKeys.forEach(key => {
          if(data[key] && typeof data[key] == 'object') data[key] = Object.assign({}, data[key], { fecha_version: versionDate(data[key]) })
        })
        return data
      },
      // sistema e intranet: estado (punto) y antigüedad de la versión
      apps() {
        if(!this.status) return []
        var list = [{ key: 'sistema', label: this.appLabel, app: this.status.sistema }]
        if(this.status.intranet) list.push({ key: 'intranet', label: 'Sitio web', app: this.status.intranet })
        return list.map(item => ({
          key: item.key,
          label: item.label,
          tone: item.app.estado ? (item.app.estado == 'activo' ? 'success' : 'danger') : 'neutral',
          age: versionAge(item.app.fecha_version),
        }))
      },
      domainsDown() {
        return this.status ? this.status.dominios.filter(domain => !domain.apunta) : []
      },
      tooltip() {
        if(!this.status) return ''
        var esc = this.escape
        var row = (label, value) => '<tr><th>' + label + '</th><td>' + value + '</td></tr>'
        var appRows = (title, app) => {
          var rows = '<tr class="monitor-status-tip__title"><th colspan="2">' + title + '</th></tr>'
          if(app.estado) rows += row('Estado', app.estado == 'activo' ? '<span class="monitor-status-tip__ok">● Activo</span>' : '<span class="monitor-status-tip__bad">● Inactivo</span>')
          rows += row('Versión', esc(app.version))
          rows += row('Actualizada', app.fecha_version ? formatDate(app.fecha_version) + ' (' + timeAgo(app.fecha_version) + ')' : 'sin dato')
          return rows
        }
        var html = appRows(this.appLabel, this.status.sistema)
        if('intranet' in this.status) {
          html += this.status.intranet ? appRows('Sitio web', this.status.intranet) : '<tr class="monitor-status-tip__title"><th colspan="2">Sitio web</th></tr>' + row('', 'No tiene')
        }
        html += '<tr class="monitor-status-tip__title"><th colspan="2">Dominios</th></tr>'
        if(!this.status.dominios.length) html += row('', 'Sin dominios')
        this.status.dominios.forEach(domain => {
          html += row(domain.apunta ? '<span class="monitor-status-tip__ok">✓</span>' : '<span class="monitor-status-tip__bad">✗</span>', esc(domain.dominio) + (domain.apunta ? '' : ' <span class="monitor-status-tip__bad">(no apunta)</span>'))
        })
        if(this.status.fecha_actualizacion) {
          html += '<tr class="monitor-status-tip__title"><th colspan="2">Relevado</th></tr>' + row('', formatDate(this.status.fecha_actualizacion) + ' (' + timeAgo(this.status.fecha_actualizacion) + ')')
        }
        html += '<tr class="monitor-status-tip__legend"><td colspan="2">Antigüedad: verde hasta ' + VERSION_FRESH_DAYS + ' días, amarillo hasta ' + VERSION_OLD_DAYS + ', rojo más.</td></tr>'
        return '<table class="monitor-status-tip__table">' + html + '</table>'
      },
    },
    methods: {
      timeAgo,
      formatDate,
      // info completa para el tooltip de la tabla (fechas en d/m/Y)
      appLine(label, app) {
        var parts = []
        if(app.estado) parts.push(this.stateLabel(app.estado))
        parts.push('versión ' + this.escape(app.version))
        if(app.fecha_version) parts.push('actualizada el ' + formatDate(app.fecha_version) + ' (' + timeAgo(app.fecha_version) + ')')
        return '<b>' + label + ':</b> ' + parts.join(' · ')
      },
      stateVariant(state) {
        return STATES[state] ? STATES[state].variant : 'secondary'
      },
      stateLabel(state) {
        return STATES[state] ? STATES[state].label : state
      },
      escape(text) {
        return String(text || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
      },
    }
  }
</script>

<style scoped>
  .monitor-status-cell {
    display: inline-flex;
    flex-direction: column;
    gap: 3px;
    cursor: default;
  }

  .monitor-status-cell__app {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    font-size: 12px;
  }

  .monitor-status-cell__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex: 0 0 9px;
  }

  .monitor-status-cell__dot--success { background: var(--app-color-success); box-shadow: 0 0 0 3px var(--app-color-success-soft); }
  .monitor-status-cell__dot--danger { background: var(--app-color-danger); box-shadow: 0 0 0 3px var(--app-color-danger-soft); }
  .monitor-status-cell__dot--neutral { background: var(--app-color-text-muted); }

  .monitor-status-cell__name {
    min-width: 70px;
    font-weight: 600;
  }

  .monitor-status-cell__age {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 0 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
  }

  .monitor-status-cell__age--success { background: var(--app-color-success-soft); color: var(--app-color-success); }
  .monitor-status-cell__age--warning { background: var(--app-color-warning-soft); color: var(--app-color-warning-hover); }
  .monitor-status-cell__age--danger { background: var(--app-color-danger-soft); color: var(--app-color-danger); }
  .monitor-status-cell__age--none { color: var(--app-color-text-muted); font-weight: 400; }

  .monitor-status-detail__age {
    font-size: 12px;
    font-weight: 400;
  }
</style>
